<template>
  <div
    :class="['date-picker', { standalone: type === 'standalone' }]"
  >
    <label
      :class="['label', { hidden: hideLabel, disabled: disabled }]"
    >
      {{ label }}
    </label>
    <template v-for="key in Object.keys(date)">
      <listbox
        :key="`custom-${key}`"
        class="custom-datepicker"
        :label="capitalize(key)"
        :options="getOptionValues(key)"
        :type="key"
        :value="date[key]"
        :disabled="disabled"
        @change="handleListboxDataChange"
      />
      <div
        :key="`native-${key}`"
        class="native-datepicker"
      >
        <select
          :key="key"
          :value="date[key]"
          :class="key"
          :disabled="disabled"
          @change="handleDateChange($event, key)"
        >
          <option
            value="0"
            disabled
          >
            {{ capitalize(key) }}
          </option>
          <template v-for="(value, index) in defaultValues[key]">
            <option
              :key="index"
              :value="getOptionValue(value, index, key)"
            >
              {{ value }}
            </option>
          </template>
        </select>
        <div />
      </div>
    </template>
  </div>
</template>

<script>
import { getDaysInMonth } from 'date-fns';
import * as Sentry from '@sentry/browser';

import Listbox from '@components/singletons/listbox';
import GlobalMixin from '@/mixins/global';

import { capitalize } from '@/utils';

export default {
  components: { Listbox },
  mixins: [GlobalMixin],
  props: {
    customYears: {
      type: Array,
    },
    date: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    type: {
      type: String,
    },
  },
  computed: {
    defaultValues() {
      return {
        day: this.getDays(),
        month: this.months,
        year: this.customYears || this.years ,
      };
    },
  },
  methods: {
    capitalize,
    getOptionValue(value, index, key) {
      if (key === 'year') {
        return value;
      } else {
        return index + 1;
      }
    },
    getOptionValues(key) {
      return this.defaultValues[key].map((value, index) => {
        return {
          text: value,
          value: this.getOptionValue(value, index, key),
        };
      });
    },
    getDays() {
      let days = 31;
      if (this.date.month) {
        // technically without year we could get a feb 29 incorrectly, but oh well
        days = getDaysInMonth(new Date(this.date.year, this.date.month - 1));
      }

      return [...Array(days).keys()].map(n => ++n);
    },
    handleDateChange(event, key) {
      const val = parseInt(event.target.value);
      if (isNaN(val)) {
        Sentry.withScope(scope => {
          scope.setLevel('info');
          scope.setExtra('value', event.target.value);
          Sentry.captureMessage('DatePicker Value Error');
        });
      }
      this.$emit('date-change', key, val);
    },
    handleListboxDataChange(value, key) {
      this.$emit('date-change', key, value);
    },
  },
};
</script>

<style lang="scss" scoped>
  .date-picker {
    &:first-of-type {
      select {
        margin-top: 0;
        @include breakpoint(medium) {
          margin-top: rem-calc(4);
        }
      }
    }

    .label {
      &:not(.hidden) {
        display: inline-block;
        font-size: rem-calc(14);
        font-weight: $semi-bold;
        width: rem-calc(100);
        @include breakpoint(medium) {
          display: block;
        }
      }
      &.hidden {
        position: absolute;
        top: 0;
        left: -10000px;
      }
      &.disabled {
        color: #989898;
      }
    }

    .native-datepicker {
      display: none;

    }

    .custom-datepicker {
      display: inline-block;
      margin-bottom: rem-calc(8);
    }

    @media (max-device-width: 40em) {
      .native-datepicker {
        display: inline-block;
      }

      .custom-datepicker {
        display: none;
      }
    }


    select {
      @include select-base;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' version='1.1' x='0px' y='0px' width='16px' height='8px' viewBox='0 0 960 560' enable-background='new 0 0 960 560' xml:space='preserve'%3E%3Cg%3E%3Cpath xmlns='http://www.w3.org/2000/svg' fill='%23a8aab7 ' d='M480,344.181L268.869,131.889c-15.756-15.859-41.3-15.859-57.054,0c-15.754,15.857-15.754,41.57,0,57.431l237.632,238.937 c8.395,8.451,19.562,12.254,30.553,11.698c10.993,0.556,22.159-3.247,30.555-11.698l237.631-238.937 c15.756-15.86,15.756-41.571,0-57.431s-41.299-15.859-57.051,0L480,344.181z'/%3E%3C/g%3E%3C/svg%3E");
      background-position: calc(100% - 5px) center;
      background-repeat: no-repeat;
      border-radius: 4px;
      border: solid 1px #dfe3e9;
      font-size: rem-calc(16); // must be 16px or greater to avoid ios safari autozoom
      font-weight: $semi-bold;
      margin: rem-calc(4) 0;
      max-width: rem-calc(200);
      padding: rem-calc(10) rem-calc(12);

      &:not(:first-of-type) {
        margin-left: rem-calc(8);
      }

      &.month {
        min-width: rem-calc(115);
      }

      &.day,
      &.year {
        min-width: rem-calc(78);
      }
    }

    &.standalone {
      margin-bottom: rem-calc(24);

      &:first-of-type {
        select {
          margin-top: rem-calc(4);
        }
      }

      label {
        color: #7f8fa4;
        margin-bottom: rem-calc(8);
        display: block;
      }
    }
  }
</style>